import React from 'react';
import Link from 'next/link';
import cityData from '@/public/city_list.json';
import usePrefix from '@/hooks/usePrefix';

type CityLocation = {
  Region: string;
  facility_id: number | null;
  facility_url?: string | null;
  facility_name: string;
};

const areas = (cityData.city_list as CityLocation[]).reduce<
  Array<{ name: string; locations: CityLocation[] }>
>((groupedAreas, location) => {
  const currentArea = groupedAreas.find(
    (area) => area.name === location.Region
  );

  if (currentArea) {
    currentArea.locations.push(location);
  } else {
    groupedAreas.push({ name: location.Region, locations: [location] });
  }

  return groupedAreas;
}, []);

export default function CityArea() {
  const prefix = usePrefix();

  return (
    <section className="container mx-auto mt-8 px-4">
      <div className="rounded-sm border border-gray-200 bg-[#fbf5ef] px-4 py-8 sm:px-7 lg:px-8">
        <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">
              Cheap Parking In Manhattan, New York
            </h2>
            <p className="mt-2 text-sm text-gray-600 sm:text-base">
              We have convenient and low cost
              <Link
                href={prefix + '/parking/Times-Square-Parking'}
                passHref
                className="text-primary hover:underline"> parking near Times Square </Link>
              and all across Manhattan with guaranteed spots and free cancellation.
            </p>
          </div>
          <p className="self-start whitespace-nowrap rounded-full bg-primary/10 px-3 py-1 text-sm font-bold text-primary">
            {areas.length} areas · {cityData.city_list.length} locations
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {areas.map((area) => (
            <article
              key={area.name}
              className="min-w-0 rounded-lg border border-[#e9dfd5] bg-white p-4 shadow-sm"
            >
              <div className="mb-3 flex min-h-[3rem] items-start justify-between gap-2">
                <h3 className="text-sm font-bold leading-5 text-gray-900">
                  {area.name}
                </h3>
                <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">
                  {area.locations.length}
                </span>
              </div>
              <ul className="grid grid-cols-1 lg:grid-cols-2 gap-x-3 gap-y-2">
                {area.locations.map((location) => {
                  const hasParkingAtRoute =
                    location.facility_id && location.facility_url;
                  return (
                    <li
                      key={location.facility_id || location.facility_name}
                      className="min-w-0 truncate text-xs text-gray-600"
                      title={location.facility_name}
                    >
                      {hasParkingAtRoute ? (
                        <Link
                          href={`${prefix}/${location.facility_url}`}
                          className="hover:text-primary hover:underline focus:text-primary focus:underline"
                        >
                          {location.facility_name}
                        </Link>
                      ) : (
                        location.facility_name
                      )}
                    </li>
                  );
                })}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
